import { useRef, useState, useEffect, useMemo } from "react";
import { Play, Pause, Download } from "lucide-react";
import { API } from "@/lib/api";

export function AudioPlayer({ src, callId }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [error, setError] = useState(false);

  // Route recording through backend proxy to bypass upstream CORS.
  // Token is passed as query param because <audio> can't set custom headers.
  const proxyUrl = useMemo(() => {
    if (!src || !callId) return null;
    const token = localStorage.getItem("cs_token");
    if (!token) return src; // no token — fall back to direct (auth-less) URL
    return `${API}/vapi/recording/${encodeURIComponent(callId)}?token=${encodeURIComponent(token)}`;
  }, [src, callId]);

  useEffect(() => {
    setError(false);
    setPlaying(false);
    setProgress(0);
    setCurrent(0);
  }, [src]);

  const toggle = async () => {
    if (!audioRef.current || !src) return;
    try {
      if (playing) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
    } catch (e) {
      setError(true);
    }
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setCurrent(a.currentTime);
    setDuration(a.duration);
    setProgress((a.currentTime / a.duration) * 100);
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  if (!src) {
    return <span className="text-xs text-muted-foreground italic">No recording</span>;
  }

  return (
    <div className="flex items-center gap-2 w-full max-w-[260px]" data-testid={`audio-player-${callId}`}>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        data-testid={`audio-play-btn-${callId}`}
        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div
          onClick={onSeek}
          className="h-1.5 bg-muted rounded-full overflow-hidden cursor-pointer relative"
          data-testid={`audio-progress-${callId}`}
        >
          <div className="h-full bg-primary transition-[width] duration-100" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-muted-foreground">
          <span>{fmt(current)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>
      {error && <span className="text-[10px] text-destructive">err</span>}
      <a
        href={proxyUrl}
        download
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        aria-label="Download recording"
        data-testid={`audio-download-btn-${callId}`}
        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <Download className="w-3.5 h-3.5" />
      </a>
      <audio
        ref={audioRef}
        src={proxyUrl}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); setCurrent(0); }}
        onTimeUpdate={onTime}
        onLoadedMetadata={onTime}
        onError={() => setError(true)}
        preload="metadata"
      />
    </div>
  );
}
